"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "@/lib/utils";
import { Input } from "components/ui/v2/input";
import {
  fieldVariantClasses,
  type FieldVariant,
} from "components/ui/v2/field-variants";
import { Z_INDEX } from "components/ui/v2/z-index";
import { Check, ChevronDown, Search, X } from "lucide-react";
import * as React from "react";

export interface MultiSelectFilterOption {
  value: string;
  label: string;
}

export interface MultiSelectFilterV2Props {
  options: MultiSelectFilterOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  clearLabel?: string;
  selectAllLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  variant?: FieldVariant;
  compact?: boolean;
  /** Tamaño: "xs" | "sm" | "filter" (w-64 h-11). Default: "sm" */
  size?: "xs" | "sm" | "filter";
  /** Si true, solo compacta el panel; el trigger mantiene h-11 para alinear con Input sm. */
  compactPanelOnly?: boolean;
  /** Texto del pie del panel (p. ej. "51 disponibles" o "2 / 51"). */
  footerCountLabel?: (ctx: {
    selected: number;
    total: number;
    filtered: number;
  }) => string;
  /** Resumen cuando hay 2+ seleccionados. */
  summaryLabel?: (count: number) => string;
  /** Label flotante (mismo patrón que Input v2). Si se define, reemplaza el placeholder en reposo. */
  label?: string;
  icon?: React.ReactNode;
}

function normalize(value: string) {
  return value.trim().toLowerCase();
}

export function MultiSelectFilterV2({
  options,
  value,
  onChange,
  placeholder = "Seleccionar...",
  searchPlaceholder = "Buscar...",
  emptyText = "Sin resultados",
  clearLabel = "Limpiar",
  selectAllLabel = "Seleccionar visibles",
  disabled = false,
  loading = false,
  variant = "subtle",
  compact = false,
  size = "sm",
  compactPanelOnly = false,
  footerCountLabel,
  summaryLabel,
  label,
  icon,
}: MultiSelectFilterV2Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const colors = fieldVariantClasses(variant);

  const selectedSet = React.useMemo(() => new Set(value), [value]);

  const filteredOptions = React.useMemo(() => {
    const q = normalize(query);
    if (!q) return options;
    return options.filter((option) => normalize(option.label).includes(q));
  }, [options, query]);

  const selectedLabels = React.useMemo(
    () =>
      value
        .map((id) => options.find((option) => option.value === id)?.label)
        .filter(Boolean) as string[],
    [options, value]
  );

  const triggerLabel = React.useMemo(() => {
    if (loading) return placeholder;
    if (value.length === 0) return placeholder;
    if (value.length === 1) return selectedLabels[0] ?? placeholder;
    if (value.length === 2) {
      const [a, b] = selectedLabels;
      return a && b ? `${a}, ${b}` : summaryLabel?.(value.length) ?? `${value.length} seleccionados`;
    }
    return summaryLabel?.(value.length) ?? `${value.length} seleccionados`;
  }, [loading, placeholder, selectedLabels, summaryLabel, value.length]);

  const footerLabel =
    footerCountLabel?.({
      selected: value.length,
      total: options.length,
      filtered: filteredOptions.length,
    }) ??
    (value.length > 0
      ? `${value.length} / ${options.length}`
      : `${options.length}`);

  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) => selectedSet.has(option.value));

  const someFilteredSelected = filteredOptions.some((option) =>
    selectedSet.has(option.value)
  );

  const toggleOption = (optionValue: string) => {
    onChange(
      selectedSet.has(optionValue)
        ? value.filter((id) => id !== optionValue)
        : [...value, optionValue]
    );
  };

  const handleSelectAllFiltered = () => {
    const next = new Set(value);
    filteredOptions.forEach((option) => next.add(option.value));
    onChange(Array.from(next));
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  const panelCompact = compact || compactPanelOnly;
  const isXs = size === "xs";
  const isSm = size === "sm";
  const isFilter = size === "filter";
  const triggerHeight = isXs
    ? "h-9"
    : isSm || (panelCompact && !compactPanelOnly)
      ? "h-10"
      : "h-11";
  const triggerWidth = isFilter ? "w-64" : "w-full";
  const triggerText = isXs
    ? "text-xs"
    : isSm || isFilter || (panelCompact && !compactPanelOnly)
      ? "text-xs"
      : "text-sm";
  const hasValue = value.length > 0;
  const isFloating = open || hasValue || (loading && Boolean(label));
  const labelLeft = icon ? (isXs ? "left-8" : isSm ? "left-9" : "left-10") : isXs ? "left-2.5" : isSm ? "left-3" : "left-4";

  const valueText = loading
    ? placeholder
    : hasValue
      ? triggerLabel
      : label
        ? ""
        : placeholder;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <div className={cn("relative", triggerWidth)}>
        <PopoverPrimitive.Trigger asChild disabled={disabled || loading}>
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={label}
            className={cn(
              "relative flex items-center rounded-2xl border bg-white/70 text-left shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-200 outline-none",
              triggerWidth,
              "hover:bg-white/90 dark:bg-zinc-950/70 dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)] dark:hover:bg-zinc-950/90",
              open && "pointer-events-none opacity-0",
              "focus-visible:ring-1 focus-visible:ring-offset-0",
              colors.border,
              colors.focusBorder,
              colors.focusRing,
              open && cn("ring-1 ring-offset-0", colors.focusRing.replace(/focus:/g, "")),
              triggerHeight,
              triggerText,
              icon ? (isXs ? "pl-8" : isSm ? "pl-9" : "pl-10") : isXs ? "pl-2.5" : isSm ? "pl-3" : "pl-4",
              hasValue ? (isXs ? "pr-14" : isSm ? "pr-14" : "pr-16") : isXs ? "pr-8" : isSm ? "pr-9" : "pr-10",
              (disabled || loading) && "cursor-not-allowed opacity-50"
            )}
          >
            {icon ? (
              <span
                className={cn(
                  "pointer-events-none absolute top-1/2 z-10 -translate-y-1/2 text-muted-foreground",
                  isXs ? "left-2.5 h-3.5 w-3.5" : isSm ? "left-3 h-3.5 w-3.5" : "left-3.5 h-4 w-4"
                )}
              >
                {icon}
              </span>
            ) : null}

            {label ? (
              <span
                className={cn(
                  "pointer-events-none absolute z-20 max-w-[calc(100%-3rem)] truncate select-none font-medium transition-all duration-200",
                  labelLeft,
                  isFloating
                    ? cn(
                        isXs
                          ? "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1.5 py-0.5 text-[9px] leading-none dark:bg-zinc-950/90"
                          : isSm
                            ? "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-1.5 py-0.5 text-[10px] leading-none dark:bg-zinc-950/90"
                            : "top-0 -translate-y-1/2 rounded-2xl bg-white/90 px-2 py-1 text-[10px] leading-none dark:bg-zinc-950/90",
                        colors.label
                      )
                    : cn("top-1/2 -translate-y-1/2 text-muted-foreground", isXs ? "text-[10px]" : isSm || isFilter ? "text-xs" : "text-[13px]")
                )}
              >
                {label}
              </span>
            ) : null}

            <span
              className={cn(
                "min-w-0 flex-1 truncate leading-5",
                hasValue ? "pr-8" : undefined,
                hasValue || (loading && label)
                  ? "font-medium text-foreground"
                  : !label
                    ? "text-slate-400 dark:text-zinc-500"
                    : "text-transparent"
              )}
            >
              {valueText}
            </span>

            {hasValue && !loading ? (
              <span className={cn("absolute top-1/2 shrink-0 -translate-y-1/2 rounded-full bg-slate-500/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-slate-600 dark:text-zinc-300", isXs ? "right-11" : isSm ? "right-11" : "right-14")}>
                {value.length}
              </span>
            ) : null}

            <ChevronDown
              className={cn(
                "pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 transition-transform duration-200 dark:text-zinc-500",
                isXs ? "right-2.5" : isSm ? "right-3" : "right-3.5",
                open && "rotate-180"
              )}
            />
          </button>
        </PopoverPrimitive.Trigger>

        {hasValue && !loading && !disabled ? (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onChange([]);
            }}
            className={cn(
              "absolute top-1/2 z-10 -translate-y-1/2 rounded p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-100",
              isXs ? "right-6" : isSm ? "right-7" : "right-8"
            )}
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          side="bottom"
          sideOffset={isXs ? -36 : isSm ? -40 : -44}
          collisionPadding={10}
          className={cn(
            "msf-panel z-[3000] overflow-hidden rounded-[20px] border border-slate-200/70",
            panelCompact
              ? "w-[min(var(--radix-popover-trigger-width),13rem)] min-w-[12rem]"
              : "w-[var(--radix-popover-trigger-width)] min-w-[14rem]",
            "duration-300",
            "dark:border-zinc-800/80"
          )}
          style={{ zIndex: Z_INDEX.DROPDOWN }}
        >
          <div className={cn(
            "flex items-center border-b border-slate-200/60 px-1.5 dark:border-zinc-800/80",
            isXs ? "h-9" : isSm ? "h-10" : "h-11"
          )}>
            <Input
              size="xxs"
              variant="subtle"
              label={searchPlaceholder}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              leftIcon={<Search className="h-3 w-3" />}
              rightIcon={
                query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="pointer-events-auto text-slate-400 hover:text-slate-700 dark:hover:text-zinc-100"
                    aria-label={clearLabel}
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : null
              }
            />
          </div>

          <div
            className={cn(
              "overflow-y-auto overscroll-contain p-1",
              panelCompact ? "max-h-56" : "max-h-64"
            )}
            role="listbox"
            aria-multiselectable
          >
            {filteredOptions.length === 0 ? (
              <p className="px-2 py-4 text-center text-[11px] text-slate-500 dark:text-zinc-400">
                {emptyText}
              </p>
            ) : (
              filteredOptions.map((option) => {
                const checked = selectedSet.has(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggleOption(option.value)}
                    className={cn(
                      "flex w-full items-center gap-1.5 rounded-lg px-2 py-1 text-left text-[12px] leading-tight transition-all duration-150",
                      "hover:bg-indigo-50/60 dark:hover:bg-indigo-950/30",
                      checked && "bg-indigo-100/40 dark:bg-indigo-950/50"
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex h-2 w-2 shrink-0 items-center justify-center rounded-[3px] border transition-all duration-150",
                        checked
                          ? "border-indigo-500 bg-indigo-500 text-white shadow-[0_0_0_3px_rgba(99,102,241,0.14)]"
                          : "border-slate-300 bg-white/70 dark:border-zinc-700 dark:bg-zinc-900/70"
                      )}
                    >
                      {checked ? <Check className="h-1.5 w-1.5" strokeWidth={3.5} /> : null}
                    </span>
                    <span className="min-w-0 flex-1 font-normal tracking-[-0.01em] text-foreground/90">
                      {option.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t border-slate-200/60 px-2 py-1 dark:border-zinc-800/80">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="min-w-0 truncate text-[10px] leading-4 text-slate-500 dark:text-zinc-400">
                {footerLabel}
              </span>
              {value.length > 0 ? (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="shrink-0 rounded-md px-1 py-0.5 text-[9px] font-medium leading-none text-slate-600 transition-colors hover:bg-slate-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                >
                  {clearLabel}
                </button>
              ) : null}
            </div>
            {filteredOptions.length > 0 && someFilteredSelected && !allFilteredSelected ? (
              <button
                type="button"
                onClick={handleSelectAllFiltered}
                className="mt-0.5 block w-full truncate rounded-md px-1 py-0.5 text-left text-[9px] font-medium leading-none text-indigo-600 transition-colors hover:bg-indigo-500/10 dark:text-indigo-300"
              >
                {selectAllLabel}
              </button>
            ) : null}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>

      <style jsx global>{`
        .msf-panel {
          isolation: isolate;
          transform-origin: var(--radix-popover-content-transform-origin, top left);
          background: rgba(255, 255, 255, 0.65);
          backdrop-filter: blur(20px) saturate(1.3);
          -webkit-backdrop-filter: blur(20px) saturate(1.3);
          box-shadow:
            inset 0 0 0 1px rgba(15, 23, 42, 0.08),
            0 1px 2px rgba(15, 23, 42, 0.04),
            0 12px 28px -10px rgba(15, 23, 42, 0.22),
            0 24px 48px -20px rgba(15, 23, 42, 0.18);
        }
        .dark .msf-panel {
          background: rgba(16, 16, 20, 0.84);
          backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          -webkit-backdrop-filter: blur(20px) saturate(1.02) brightness(0.66);
          box-shadow:
            inset 0 0 0 1px rgba(255, 255, 255, 0.03),
            0 1px 2px rgba(0, 0, 0, 0.4),
            0 12px 28px -10px rgba(0, 0, 0, 0.55),
            0 24px 48px -20px rgba(0, 0, 0, 0.45);
        }
        .msf-panel[data-state="open"] {
          animation: msfMorphOpen 360ms cubic-bezier(0.34, 1.2, 0.64, 1) both;
        }
        .msf-panel[data-state="closed"] {
          animation: msfMorphClose 220ms cubic-bezier(0.34, 1.2, 0.64, 1) both;
        }
        @keyframes msfMorphOpen {
          0% {
            opacity: 0.04;
            clip-path: inset(0 0 calc(100% - var(--radix-popover-trigger-height, 36px)) 0 round 18px);
            transform: scaleX(0.98) scaleY(0.78);
          }
          58% {
            opacity: 1;
            transform: scaleX(1.01) scaleY(1.015);
          }
          100% {
            opacity: 1;
            clip-path: inset(0 0 0 0 round 20px);
            transform: scaleX(1) scaleY(1);
          }
        }
        @keyframes msfMorphClose {
          0% {
            opacity: 1;
            clip-path: inset(0 0 0 0 round 20px);
            transform: scaleX(1) scaleY(1);
          }
          100% {
            opacity: 0;
            clip-path: inset(0 0 calc(100% - var(--radix-popover-trigger-height, 36px)) 0 round 18px);
            transform: scaleX(0.98) scaleY(0.78);
          }
        }
      `}</style>
    </PopoverPrimitive.Root>
  );
}
